/**
 * DashboardSelector — GCP-style auto-hiding left sidebar.
 *
 * Behavior:
 *   - Collapsed by default: shows only a thin rail with icons (48px)
 *   - On hover: slides open to full width (220px) with labels, with a
 *     subtle overlay shadow on the content area
 *   - On mouse leave: slides back to the mini-rail
 *   - Selected item has a left accent bar + background highlight
 *   - Smooth CSS transitions (no JS timers)
 *
 * Inspired by GCP's navigation sidebar UX.
 */

import React, { useState } from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { DashboardPlugin } from "./types";

interface DashboardSelectorProps {
  dashboards: DashboardPlugin[];
  selectedId: string;
  onSelect: (id: string) => void;
}

const EXPANDED_WIDTH = 220;
const COLLAPSED_WIDTH = 48;
const TRANSITION = "width 200ms cubic-bezier(0.4, 0, 0.2, 1)";

const DashboardSelector: React.FC<DashboardSelectorProps> = ({
  dashboards,
  selectedId,
  onSelect,
}) => {
  const theme = useTheme();
  const [hovered, setHovered] = useState(false);

  if (dashboards.length <= 1) return null;

  const width = hovered ? EXPANDED_WIDTH : COLLAPSED_WIDTH;

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        position: "relative",
        width,
        minWidth: width,
        transition: TRANSITION,
        borderRight: 1,
        borderColor: "divider",
        height: "100%",
        bgcolor:
          theme.palette.mode === "dark" ? "grey.900" : "background.paper",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        zIndex: 10,
        // Subtle shadow when expanded to indicate overlay feel
        boxShadow: hovered ? "4px 0 12px rgba(0,0,0,0.08)" : "none",
      }}
    >
      {/* Header — only visible when expanded */}
      <Box
        sx={{
          px: 2,
          pt: 2,
          pb: 1,
          opacity: hovered ? 1 : 0,
          transition: "opacity 150ms ease",
          whiteSpace: "nowrap",
        }}
      >
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ fontWeight: 700, letterSpacing: 1 }}
        >
          Dashboards
        </Typography>
      </Box>

      <List disablePadding sx={{ flex: 1 }}>
        {dashboards.map((d) => {
          const isSelected = d.id === selectedId;
          return (
            <ListItemButton
              key={d.id}
              selected={isSelected}
              onClick={() => onSelect(d.id)}
              sx={{
                py: 1.5,
                px: hovered ? 2 : 0,
                justifyContent: hovered ? "flex-start" : "center",
                transition: "padding 200ms ease, justify-content 200ms ease",
                borderLeft: isSelected ? 3 : 0,
                borderColor: isSelected ? "primary.main" : "transparent",
                "&.Mui-selected": {
                  bgcolor:
                    theme.palette.mode === "dark" ? "grey.800" : "primary.50",
                },
                "&:hover": {
                  bgcolor:
                    theme.palette.mode === "dark" ? "grey.800" : "grey.100",
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: hovered ? 32 : 0,
                  mr: hovered ? 1 : 0,
                  justifyContent: "center",
                  color: isSelected ? "primary.main" : "text.secondary",
                  transition: "min-width 200ms ease, margin 200ms ease",
                }}
              >
                {d.icon}
              </ListItemIcon>
              {hovered && (
                <ListItemText
                  primary={d.displayName}
                  primaryTypographyProps={{
                    variant: "body2",
                    fontWeight: isSelected ? 700 : 500,
                    noWrap: true,
                    color: isSelected ? "primary.main" : "text.primary",
                  }}
                />
              )}
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );
};

export { EXPANDED_WIDTH, COLLAPSED_WIDTH };
export default DashboardSelector;
